import { faTrashXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box } from "@mui/material"
import Tooltip from "@mui/material/Tooltip"
import MuiAutoComplete from "components/MuiAutocomplete"
import { MuiTableColumn } from "components/MuiTable"
import MuiTextField from "components/MuiTextField"
import { ProjectFormPageProps } from "components/ProjectForm"
import { setLogisticsBasicDetailsForm } from "components/ProjectForm/ProjectFormSlice"
import { Option } from "components/MuiAutocomplete/shared"
import { EAccountRole } from "enums/commonEnums"
import EModuleIds from "enums/ModuleEnum"
import { FormikErrors, useFormik } from "formik"
import useProjectCategoryOptions from "hooks/useProjectCategoryOptions"
import { enqueueSnackbar } from "notistack"
import {
    AccountBillingAddressDetails,
    AccountContactDetails,
    useLazyGetAccountDetailsQuery,
} from "pages/Accounts/AccountsServices"
import {
    useAddPurchaseOrderMutation,
    useGetBrandsQuery,
    useLazyGetAccountsCompactedQuery,
    useGetDealDoneByUsersQuery,
    useGetProductsQuery,
    useGetPurchaseOrdersQuery,
    useGetTimeOfDayQuery,
} from "pages/Common/commonService"
import { DropdownObj } from "pages/Common/commonTypes"
import { useLazyGetAllComplaintsQuery } from "pages/Complaints/Services/ComplaintsService"
import { EComplaintLinkSource, IComplaintSummary } from "pages/Complaints/types/complaintLogistics"
import {
    buildComplaintPickerQueryArg,
    complaintOptionLabel,
    hasMoreComplaintPages,
    mapComplaintRowToSummary,
} from "pages/Complaints/utils/complaintLogisticsPicker"
import { useGetLogisticsDataQuery } from "pages/Logistics/LogisticsServices"
import { useParseChargersFileMutation, useParseMaterialsFileMutation } from "pages/Projects/ProjectServices"
import { useGetReportTemplatesQuery } from "pages/Settings/SettingsService"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { TStore } from "store/Store"
import { array, mixed, number, object, ObjectSchema, string } from "yup"
import { findComplaintDrawerCategoryOption, isComplaintLinkedCategory } from "./complaintLinkedCategories"
import {
    announceParseResult,
    ChargerRow,
    ChargersAndMaterialsFormValues,
    EChargersAndMaterialsFields,
    expandParsedChargerRows,
    MaterialRow,
    resolveSkuOption,
} from "../../ChargersAndMaterials/useChargersAndMaterials"
import {
    EPurchaseOrderFields,
    EPurchaseOrderItemFields,
    PurchaseOrderFormValues,
    PurchaseOrderItem,
} from "../Projects/useProjectBasicDetails"

export enum ELogisticBasicDetailFields {
    PROJECT_NAME = "project_name",
    CATEGORY = "category_id",
    PURCHASE_ORDER = "purchase_order",
    VISIT_BY_DATE = "visit_by_date",
    VISIT_BY_TIME = "visit_by_time",
    SOURCE_POC = "source_poc",
    DESTINATION_POC = "destination_poc",
    /** The complaint this dispatch is for. Mounts only for the complaint-linked categories. */
    COMPLAINT = "complaint_id",
    /** Identifying fields of the picked complaint, held so the read-only summary card can render
     *  without a second lookup and survives navigating back to this step. */
    COMPLAINT_SUMMARY = "complaint_summary",
}

type IPocDetailsForm = {
    on_ground_poc_name?: string
    on_ground_poc_phone?: string
    on_ground_poc_email?: string
}

export type LogisticBasicDetailsFormValues = {
    [ELogisticBasicDetailFields.PROJECT_NAME]: string
    [ELogisticBasicDetailFields.CATEGORY]: DropdownObj | null
    [ELogisticBasicDetailFields.PURCHASE_ORDER]: DropdownObj | null
    [ELogisticBasicDetailFields.VISIT_BY_DATE]: string
    [ELogisticBasicDetailFields.VISIT_BY_TIME]: DropdownObj | null
    [ELogisticBasicDetailFields.SOURCE_POC]: IPocDetailsForm
    [ELogisticBasicDetailFields.DESTINATION_POC]: IPocDetailsForm
    [ELogisticBasicDetailFields.COMPLAINT]: DropdownObj | null
    [ELogisticBasicDetailFields.COMPLAINT_SUMMARY]: IComplaintSummary | null
}

const initialValues: LogisticBasicDetailsFormValues = {
    [ELogisticBasicDetailFields.PROJECT_NAME]: "",
    [ELogisticBasicDetailFields.CATEGORY]: null,
    [ELogisticBasicDetailFields.PURCHASE_ORDER]: null,
    [ELogisticBasicDetailFields.VISIT_BY_DATE]: "",
    [ELogisticBasicDetailFields.VISIT_BY_TIME]: null,
    [ELogisticBasicDetailFields.SOURCE_POC]: {
        on_ground_poc_name: "",
        on_ground_poc_email: "",
        on_ground_poc_phone: "",
    },
    [ELogisticBasicDetailFields.DESTINATION_POC]: {
        on_ground_poc_name: "",
        on_ground_poc_email: "",
        on_ground_poc_phone: "",
    },
    [ELogisticBasicDetailFields.COMPLAINT]: null,
    [ELogisticBasicDetailFields.COMPLAINT_SUMMARY]: null,
}

/** Page size for the complaint picker's server-side search. */
const COMPLAINT_PICKER_PAGE_SIZE = 20

/**
 * Project name pre-filled when raising from a complaint: `Service — <charger ID> <site name>`.
 * Editable afterwards, so this only has to be a sensible starting point.
 */
const buildServiceProjectName = (complaint: IComplaintSummary) =>
    ["Service —", complaint.charger_id, complaint.site_name].filter(Boolean).join(" ")

const initialChargerAndMaterialValues: ChargersAndMaterialsFormValues = {
    [EChargersAndMaterialsFields.CHARGER_FILE]: null,
    [EChargersAndMaterialsFields.MATERIAL_FILE]: null,
    [EChargersAndMaterialsFields.CHARGERS]: [],
    [EChargersAndMaterialsFields.MATERIALS]: [],
}

const createNewItem = (): PurchaseOrderItem => ({
    [EPurchaseOrderItemFields.SKU_CODE]: null,
    [EPurchaseOrderItemFields.QUANTITY]: "",
    [EPurchaseOrderItemFields.RATE]: "",
    [EPurchaseOrderItemFields.DISCOUNT_VALUE]: "",
    [EPurchaseOrderItemFields.DISCOUNT_TYPE]: { id: 1, title: "%" },
    [EPurchaseOrderItemFields.TAX]: "",
    [EPurchaseOrderItemFields.TOTAL_AMOUNT]: "",
})

const initialPurchaseOrderValues: PurchaseOrderFormValues = {
    [EPurchaseOrderFields.PO_NAME]: "",
    [EPurchaseOrderFields.PO_NUMBER]: "",
    [EPurchaseOrderFields.ACCOUNT]: null,
    [EPurchaseOrderFields.BILLING_ADDRESS]: null,
    [EPurchaseOrderFields.PO_DATE]: "",
    [EPurchaseOrderFields.PO_RECEIVE_DATE]: "",
    [EPurchaseOrderFields.PO_DEAL_DONE_BY]: null,
    [EPurchaseOrderFields.TOTAL_PO_VALUE]: null,
    [EPurchaseOrderFields.SHIPPING_CHARGES]: null,
    [EPurchaseOrderFields.REMARKS]: null,
    [EPurchaseOrderFields.PO_FILE]: null,
    items: [createNewItem()],
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
}).nullable()

const optionalStringSchema = string().trim().nullable().notRequired()
const requiredStringSchema = string().trim().required("This field is required")
const requiredDropdownSchema = dropdownSchema.required("This field is required")
const optionalDropdownSchema = dropdownSchema.defined()

const hasAnyPocValue = (...values: (string | undefined)[]) => values.some(value => value?.trim())

// If any field of a POC row is filled, the remaining fields of that row become required.
// The dependency pairs passed to shape() break the circular `when` references between siblings.
const POC_SCHEMA: ObjectSchema<IPocDetailsForm> = object().shape(
    {
        on_ground_poc_name: string()
            .trim()
            .when(["on_ground_poc_phone", "on_ground_poc_email"], {
                is: (phone?: string, email?: string) => hasAnyPocValue(phone, email),
                then: schema => schema.required("POC Name is required"),
                otherwise: schema => schema.optional(),
            }),

        on_ground_poc_phone: string()
            .trim()
            .matches(/^[0-9]{10}$/, { message: "Contact must be a valid 10-digit number", excludeEmptyString: true })
            .when(["on_ground_poc_name", "on_ground_poc_email"], {
                is: (name?: string, email?: string) => hasAnyPocValue(name, email),
                then: schema => schema.required("POC Contact is required"),
                otherwise: schema => schema.optional(),
            }),

        on_ground_poc_email: string()
            .trim()
            .email("Please enter a valid email")
            .when(["on_ground_poc_name", "on_ground_poc_phone"], {
                is: (name?: string, phone?: string) => hasAnyPocValue(name, phone),
                then: schema => schema.required("POC Email is required"),
                otherwise: schema => schema.optional(),
            }),
    },
    [
        ["on_ground_poc_name", "on_ground_poc_phone"],
        ["on_ground_poc_name", "on_ground_poc_email"],
        ["on_ground_poc_phone", "on_ground_poc_email"],
    ]
)

// The destination POC is the person who receives the material, so every field of it is mandatory
// irrespective of what the source POC has.
const REQUIRED_POC_SCHEMA: ObjectSchema<IPocDetailsForm> = object({
    on_ground_poc_name: string().trim().required("POC Name is required"),
    on_ground_poc_phone: string()
        .trim()
        .required("POC Contact is required")
        .matches(/^[0-9]{10}$/, "Contact must be a valid 10-digit number"),
    on_ground_poc_email: string().trim().required("POC Email is required").email("Please enter a valid email"),
})

const basicDetailsValidationSchema: ObjectSchema<LogisticBasicDetailsFormValues> = object({
    [ELogisticBasicDetailFields.PROJECT_NAME]: requiredStringSchema.label("Project Name"),
    // Mandatory per the PRD — a solo dispatch is categorised by whose site it is, which the
    // template cannot tell us, so it has to be a deliberate selection.
    [ELogisticBasicDetailFields.CATEGORY]: requiredDropdownSchema.label("Project Category"),
    [ELogisticBasicDetailFields.SOURCE_POC]: POC_SCHEMA,
    [ELogisticBasicDetailFields.DESTINATION_POC]: REQUIRED_POC_SCHEMA,
    [ELogisticBasicDetailFields.VISIT_BY_DATE]: requiredStringSchema.label("Visit by date"),
    [ELogisticBasicDetailFields.VISIT_BY_TIME]: requiredDropdownSchema.label("Visit by Time"),
    [ELogisticBasicDetailFields.PURCHASE_ORDER]: optionalDropdownSchema.label("Purchase Order"),
    // A complaint-linked dispatch exists because of a complaint, so the reference is mandatory for
    // those categories and must stay absent for every other one — the field does not even mount
    // elsewhere.
    [ELogisticBasicDetailFields.COMPLAINT]: optionalDropdownSchema
        .label("Complaint Reference ID")
        .when([ELogisticBasicDetailFields.CATEGORY], {
            is: (category: DropdownObj | null) => isComplaintLinkedCategory(category),
            then: schema => schema.nonNullable().required("Pick the complaint this dispatch is for."),
            otherwise: schema => schema,
        }),
    [ELogisticBasicDetailFields.COMPLAINT_SUMMARY]: mixed<IComplaintSummary>().nullable().defined(),
})

const purchaseOrderItemSchema = object({
    [EPurchaseOrderItemFields.SKU_CODE]: dropdownSchema.required("SKU Code is required"),
    [EPurchaseOrderItemFields.QUANTITY]: string().trim().required("Quantity is required"),
    [EPurchaseOrderItemFields.RATE]: string().trim().required("Rate is required"),
    [EPurchaseOrderItemFields.DISCOUNT_VALUE]: string().nullable().notRequired(),
    [EPurchaseOrderItemFields.DISCOUNT_TYPE]: dropdownSchema.nullable(),
    [EPurchaseOrderItemFields.TAX]: string().nullable().notRequired(),
    [EPurchaseOrderItemFields.TOTAL_AMOUNT]: string().nullable().notRequired(),
})

const purchaseOrderValidationSchema = object({
    [EPurchaseOrderFields.PO_NAME]: requiredStringSchema.label("Purchase Order Name"),
    [EPurchaseOrderFields.PO_NUMBER]: requiredStringSchema.label("Purchase Order Number"),
    [EPurchaseOrderFields.ACCOUNT]: requiredDropdownSchema.label("Account"),
    [EPurchaseOrderFields.BILLING_ADDRESS]: requiredDropdownSchema.label("Billing Address"),
    [EPurchaseOrderFields.PO_DATE]: requiredStringSchema.label("Purchase Order Date"),
    [EPurchaseOrderFields.PO_RECEIVE_DATE]: requiredStringSchema.label("Purchase Order Receive Date"),
    [EPurchaseOrderFields.PO_DEAL_DONE_BY]: requiredDropdownSchema.label("Deal Done by"),
    [EPurchaseOrderFields.TOTAL_PO_VALUE]: optionalStringSchema.label("Total Purchase Order Value"),
    [EPurchaseOrderFields.SHIPPING_CHARGES]: optionalStringSchema.label("Shipping Charges"),
    [EPurchaseOrderFields.REMARKS]: optionalStringSchema.label("Remarks"),
    [EPurchaseOrderFields.PO_FILE]: mixed<File>()
        .transform(value => (value && typeof value === "object" && Object.keys(value).length === 0 ? null : value))
        .nullable()
        .notRequired(),
    items: array().of(purchaseOrderItemSchema).min(1, "At least one item is required").defined(),
})

const CHARGER_ROW_DATA = {
    sku_code: "",
    display_name: "",
    power_rating: 0,
    branding: 0,
}

const MATERIAL_ROW_DATA = {
    sku_code: "",
    description: "",
    quantity: 0,
    remarks: "",
}

const useLogisticBasicDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const {
        account_n_sites_form_values,
        logistics_basic_details_form,
        chargers_and_materials_form,
        project_id,
        complaint_link_context,
    } = useSelector((state: TStore) => state.ProjectForm)
    const { data: purchaseOrders, isLoading: isLoadingPurchaseOrder } = useGetPurchaseOrdersQuery(
        {
            po_type: "receivable",
        },
        {
            refetchOnFocus: true,
            refetchOnMountOrArgChange: true,
        }
    )
    const [getAccountDetails] = useLazyGetAccountDetailsQuery()
    const [getAccountsCompacted] = useLazyGetAccountsCompactedQuery()
    const { data: timeOfDayList } = useGetTimeOfDayQuery()
    const { data: dealDoneByUsersList } = useGetDealDoneByUsersQuery()
    const { data: chargerProducts } = useGetProductsQuery({ is_charger: 1 })
    const { data: materialProducts } = useGetProductsQuery({ is_material: 1 })
    // Unfiltered — the PO Items SKU dropdown (skuOptions below) needs every product, not just
    // chargers, matching the Projects module's equivalent (useProjectBasicDetails.ts).
    const { data: allProducts } = useGetProductsQuery()
    const { data: brands } = useGetBrandsQuery()
    const { data: materialTemplate, isFetching: isFetchingMaterialTemplate } = useGetReportTemplatesQuery({
        isCharger: false,
    })
    const { data: chargerTemplate, isFetching: isFetchingChargerTemplate } = useGetReportTemplatesQuery({
        isCharger: true,
    })

    const [addPurchaseOrder, { isLoading: isAddingPurchaseOrder }] = useAddPurchaseOrderMutation()
    const [parseMaterialsFile, { isLoading: isParsingMaterials }] = useParseMaterialsFileMutation()
    const [parseChargersFile, { isLoading: isParsingChargers }] = useParseChargersFileMutation()

    const [showPurchaseOrderForm, setShowPurchaseOrderForm] = useState(false)
    const [billingAddressOptions, setBillingAddressOptions] = useState<DropdownObj[]>([])
    const [isFetchingBillingAddresses, setIsFetchingBillingAddresses] = useState(false)
    const [showMoreChargers, setShowMoreChargers] = useState(false)
    const [showMoreMaterials, setShowMoreMaterials] = useState(false)

    const formik = useFormik<LogisticBasicDetailsFormValues>({
        initialValues: logistics_basic_details_form
            ? {
                  ...logistics_basic_details_form,
                  source_poc: logistics_basic_details_form.source_poc || {
                      on_ground_poc_name: "",
                      on_ground_poc_email: "",
                      on_ground_poc_phone: "",
                  },
                  destination_poc: logistics_basic_details_form.destination_poc || {
                      on_ground_poc_name: "",
                      on_ground_poc_email: "",
                      on_ground_poc_phone: "",
                  },
                  // Optional on the persisted form, always present (if null) on the live one.
                  complaint_id: logistics_basic_details_form.complaint_id ?? null,
                  complaint_summary: logistics_basic_details_form.complaint_summary ?? null,
              }
            : { ...initialValues },
        validationSchema: basicDetailsValidationSchema,
        validateOnChange: true,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })
    const purchaseOrderFormik = useFormik<PurchaseOrderFormValues>({
        initialValues: { ...initialPurchaseOrderValues },
        validationSchema: purchaseOrderValidationSchema,
        validateOnChange: false,
        onSubmit: () => {},
    })
    const chargerAndMaterialsFormik = useFormik<ChargersAndMaterialsFormValues>({
        initialValues: chargers_and_materials_form
            ? {
                  ...initialChargerAndMaterialValues,
                  chargers: chargers_and_materials_form.chargers,
                  materials: chargers_and_materials_form.materials,
              }
            : initialChargerAndMaterialValues,
        validateOnChange: false,
        onSubmit: () => {},
        enableReinitialize: true,
    })

    const chargerSkuOptions = useMemo(() => {
        return (
            chargerProducts?.map(product => ({
                id: product.id,
                title: product.sku_code,
            })) || []
        )
    }, [chargerProducts])

    // Raise-form picker: system categories are written only by automations, so they are excluded.
    const {
        options: categoryOptions,
        categories,
        isFetching: isCategoriesFetching,
    } = useProjectCategoryOptions()

    // ---------------------------------------------------------------------------------------
    // Complaint linkage
    // ---------------------------------------------------------------------------------------

    /** Present only when the form was opened by "Add Logistic" from a complaint drawer. */
    const isComplaintLocked = Boolean(complaint_link_context)
    const drawerCategoryOption = useMemo(
        () => findComplaintDrawerCategoryOption(categories, categoryOptions),
        [categories, categoryOptions]
    )
    const isComplaintLinkedCategorySelected = isComplaintLinkedCategory(
        formik.values[ELogisticBasicDetailFields.CATEGORY]
    )
    const selectedComplaint = formik.values[ELogisticBasicDetailFields.COMPLAINT]
    const selectedComplaintSummary = formik.values[ELogisticBasicDetailFields.COMPLAINT_SUMMARY]

    // The picker is backed by the existing complaints list endpoint rather than a bespoke one;
    // complaintLogisticsPicker owns the parameter and row-field mapping.
    const [getComplaintsForPicker] = useLazyGetAllComplaintsQuery()
    /**
     * The picker hands back only `{ id, title }`, but the summary card needs the whole row. Cache
     * the rows as pages stream in so selecting one needs no second request.
     */
    const complaintSummariesRef = useRef<Map<number, IComplaintSummary>>(new Map())

    const [isCategoryChangeConfirmOpen, setIsCategoryChangeConfirmOpen] = useState(false)
    const [pendingCategory, setPendingCategory] = useState<DropdownObj | null>(null)

    // Non-blocking heads-up when the complaint already has dispatches against it — raising another
    // is allowed (a second part, a re-dispatch after a failed delivery), the user just gets told.
    // Uses the logistics list endpoint filtered by complaint, the same call the complaint drawer's
    // View Logistic panel makes.
    const { data: existingLinkedTickets } = useGetLogisticsDataQuery(
        {
            data: { complaint_ids: [selectedComplaint?.id], parent_status: "all" },
            selectedValue: 50,
            page: 1,
        },
        { skip: !selectedComplaint?.id || isComplaintLocked }
    )
    const existingLinkedCount = existingLinkedTickets?.data?.length ?? 0

    const complaintOptionsSelector = useCallback((res: any, page: number) => {
        // Shown as returned: the endpoint is paginated and searchable, and every filter is already
        // expressed through its own query parameters, so nothing is re-filtered here.
        const summaries: IComplaintSummary[] = (res?.data ?? []).map(mapComplaintRowToSummary)

        summaries.forEach(summary => complaintSummariesRef.current.set(summary.complaint_id, summary))

        return {
            options: summaries.map(summary => ({
                id: summary.complaint_id,
                // CIN alongside the numeric id, via the shared label so this picker, the logistics
                // list filter and the locked field all name a complaint the same way.
                title: complaintOptionLabel({
                    complaint_id: summary.complaint_id,
                    cin: summary.display_id,
                }),
                // Searchable alongside the label, so typing a site or charger still matches a row
                // whose label shows neither.
                search_key: [summary.site_name, summary.charger_id, summary.complaint_sub_type]
                    .filter(Boolean)
                    .join(" "),
            })),
            hasMore: hasMoreComplaintPages(res, page),
        }
    }, [])

    const handleComplaintChange = (value: Option | Option[] | null) => {
        const option = Array.isArray(value) ? value[0] : value
        if (!option) {
            formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT, null)
            formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT_SUMMARY, null)
            return
        }
        const complaintId = Number(option.id)
        formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT, { id: complaintId, title: option.title })
        formik.setFieldValue(
            ELogisticBasicDetailFields.COMPLAINT_SUMMARY,
            complaintSummariesRef.current.get(complaintId) ?? null
        )
    }

    /**
     * Switching away from a complaint-linked category drops the complaint reference, so it is
     * confirmed first rather than silently discarding the link the user just made.
     */
    const handleCategoryChange = (value: DropdownObj | null) => {
        if (selectedComplaint && !isComplaintLinkedCategory(value)) {
            setPendingCategory(value)
            setIsCategoryChangeConfirmOpen(true)
            return
        }
        formik.setFieldValue(ELogisticBasicDetailFields.CATEGORY, value)
    }

    const confirmCategoryChange = () => {
        formik.setFieldValue(ELogisticBasicDetailFields.CATEGORY, pendingCategory)
        formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT, null)
        formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT_SUMMARY, null)
        setIsCategoryChangeConfirmOpen(false)
        setPendingCategory(null)
    }

    const cancelCategoryChange = () => {
        setIsCategoryChangeConfirmOpen(false)
        setPendingCategory(null)
    }

    /**
     * Raised from a complaint: pre-set the locked fields, and seed the editable ones the complaint
     * can answer. Seeding is guarded on the field still being empty so a user edit is never
     * clobbered when this re-runs.
     */
    useEffect(() => {
        if (!complaint_link_context || !drawerCategoryOption) return

        if (formik.values[ELogisticBasicDetailFields.CATEGORY]?.id !== drawerCategoryOption.id) {
            formik.setFieldValue(ELogisticBasicDetailFields.CATEGORY, drawerCategoryOption)
        }
        if (formik.values[ELogisticBasicDetailFields.COMPLAINT]?.id !== complaint_link_context.complaint_id) {
            formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT, {
                id: complaint_link_context.complaint_id,
                // Same label the picker builds, so the locked field reads identically whether the
                // complaint was chosen by hand or carried in from the drawer.
                title: complaintOptionLabel({
                    complaint_id: complaint_link_context.complaint_id,
                    cin: complaint_link_context.display_id,
                }),
            })
            formik.setFieldValue(ELogisticBasicDetailFields.COMPLAINT_SUMMARY, complaint_link_context)
        }
        if (!formik.values[ELogisticBasicDetailFields.PROJECT_NAME]) {
            formik.setFieldValue(
                ELogisticBasicDetailFields.PROJECT_NAME,
                buildServiceProjectName(complaint_link_context)
            )
        }
        // The complaint does not always carry a site contact; when it doesn't, the POC fields are
        // left blank for the user to fill rather than pre-filled with something misleading.
        const destinationPoc = formik.values[ELogisticBasicDetailFields.DESTINATION_POC]
        if (!destinationPoc?.on_ground_poc_name && complaint_link_context.site_poc_name) {
            formik.setFieldValue(
                `${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_name`,
                complaint_link_context.site_poc_name
            )
        }
        if (!destinationPoc?.on_ground_poc_phone && complaint_link_context.site_poc_contact) {
            formik.setFieldValue(
                `${ELogisticBasicDetailFields.DESTINATION_POC}.on_ground_poc_phone`,
                complaint_link_context.site_poc_contact
            )
        }
    }, [complaint_link_context, drawerCategoryOption])

    const purchaseOrderOptions =
        purchaseOrders?.map(p => ({
            id: p.id,
            title: `${p.po_name} ${p.po_number ? `(${p.po_number})` : ""}`,
        })) || []

    const timeOfDayOptions = useMemo(() => {
        return timeOfDayList?.data?.map(i => ({ id: i.id, title: i.name })) || []
    }, [timeOfDayList])

    const dealDoneByUsersOptions = useMemo(() => {
        return (
            dealDoneByUsersList?.map((dealDoneByUser: any) => ({
                id: dealDoneByUser.id,
                title: dealDoneByUser.name,
            })) || []
        )
    }, [dealDoneByUsersList])

    const brandingOptions = useMemo(() => {
        return (
            brands?.map(brand => ({
                id: brand.id,
                title: brand.name,
            })) || []
        )
    }, [brands])

    const materialSkuOptions = useMemo(() => {
        return (
            materialProducts?.map(product => ({
                id: product.id,
                title: product.sku_code,
            })) || []
        )
    }, [materialProducts])

    const skuOptions = useMemo(
        () =>
            allProducts
                ? allProducts?.map(product => ({
                      id: product.id,
                      title: `${product.sku_code} - ${product.display_name}`,
                  }))
                : [],
        [allProducts]
    )

    const updateChargerRow = (rowIndex: number, key: keyof ChargerRow, value: any) => {
        const updated = [...chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.CHARGERS]]
        if (key === "sku_code") {
            const sku = chargerSkuOptions.find(option => option.id === value)
            if (sku) {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: sku.title,
                    display_name: chargerProducts?.find(product => product.id === value)?.display_name || "",
                    power_rating: chargerProducts?.find(product => product.id === value)?.charger_power_rating || 0,
                }
            } else {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: "",
                    display_name: "",
                }
            }
        } else {
            updated[rowIndex] = { ...updated[rowIndex], [key]: value }
        }
        chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const updateMaterialRow = (rowIndex: number, key: keyof MaterialRow, value: any) => {
        const updated = [...chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.MATERIALS]]
        if (key === "sku_code") {
            const sku = materialSkuOptions.find(option => option.id === value)
            if (sku) {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: sku.title,
                    description: materialProducts?.find(product => product.id === value)?.display_name || "",
                }
            } else {
                updated[rowIndex] = {
                    ...updated[rowIndex],
                    [key]: "",
                    description: "",
                }
            }
        } else {
            updated[rowIndex] = { ...updated[rowIndex], [key]: value }
        }
        chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, updated)
    }

    const handleDeleteMaterialRow = (rowIndex: number) => {
        const updated = [...chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.MATERIALS]]
        updated.splice(rowIndex, 1)
        chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, updated)
    }

    const handleCloneChargerRow = (count: number, chargerRow: ChargerRow) => {
        const updated = [...chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.CHARGERS]]
        const { plp_id, ...clonedRow } = chargerRow
        for (let i = 0; i < count; i++) {
            updated.push(clonedRow as ChargerRow)
        }
        formik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const calculatedTotalPOValue = useMemo(() => {
        const items = purchaseOrderFormik.values.items
        const itemsTotal = items.reduce((sum, item) => {
            const totalAmount = parseFloat(item[EPurchaseOrderItemFields.TOTAL_AMOUNT]) || 0
            return sum + totalAmount
        }, 0)
        const shippingCharges =
            parseFloat(purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES] || "0") || 0
        return (itemsTotal + shippingCharges).toFixed(2)
    }, [purchaseOrderFormik.values.items, purchaseOrderFormik.values[EPurchaseOrderFields.SHIPPING_CHARGES]])

    const handleDownloadTemplate = (type: "charger" | "material") => {
        const data = type === "charger" ? chargerTemplate : materialTemplate
        const link = data?.link.urls.download
        if (!link) return
        const downloadLink = document.createElement("a")
        downloadLink.href = link
        downloadLink.download = type === "charger" ? "charger_template.xlsx" : "material_template.xlsx"
        downloadLink.click()
    }

    const handleAddChargerRow = () => {
        chargerAndMaterialsFormik.setFieldValue(
            EChargersAndMaterialsFields.CHARGERS,
            chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.CHARGERS].concat([CHARGER_ROW_DATA])
        )
    }

    const handleDeleteChargerRow = (rowIndex: number) => {
        const updated = [...chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.CHARGERS]]
        updated.splice(rowIndex, 1)
        chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, updated)
    }

    const handleAddMaterialRow = () => {
        chargerAndMaterialsFormik.setFieldValue(
            EChargersAndMaterialsFields.MATERIALS,
            chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.MATERIALS].concat([MATERIAL_ROW_DATA])
        )
    }

    const handleShowMoreChargers = () => {
        if (chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.CHARGERS].length === 0) {
            handleAddChargerRow()
        }
        setShowMoreChargers(!showMoreChargers)
    }

    const handleShowMoreMaterials = () => {
        if (chargerAndMaterialsFormik.values[EChargersAndMaterialsFields.MATERIALS].length === 0) {
            handleAddMaterialRow()
        }
        setShowMoreMaterials(!showMoreMaterials)
    }

    // The material is received by the Buyer's contact picked on the Account & Site page, so the
    // destination POC is filled in from it as soon as the page opens. Anything already entered wins.
    const buyerAccountRole = account_n_sites_form_values?.accountRoles?.[EAccountRole.BUYER]
    const buyerAccountId = buyerAccountRole?.account?.id
    const buyerContactId = buyerAccountRole?.contact?.id

    useEffect(() => {
        const prefillDestinationPoc = async () => {
            if (!buyerAccountId || !buyerContactId) return

            const destinationPoc = formik.values[ELogisticBasicDetailFields.DESTINATION_POC]
            if (
                destinationPoc?.on_ground_poc_name ||
                destinationPoc?.on_ground_poc_phone ||
                destinationPoc?.on_ground_poc_email
            ) {
                return
            }

            try {
                const accountDetails = await getAccountDetails(buyerAccountId as number).unwrap()
                const contact = accountDetails.account_contacts?.find(
                    (accountContact: AccountContactDetails) => accountContact.id === buyerContactId
                )
                if (!contact) return

                formik.setFieldValue(ELogisticBasicDetailFields.DESTINATION_POC, {
                    on_ground_poc_name: contact.name || "",
                    on_ground_poc_phone: contact.phone || "",
                    on_ground_poc_email: contact.email || "",
                })
            } catch (error) {
                console.error("Error prefilling destination POC from the buyer contact:", error)
            }
        }

        prefillDestinationPoc()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerAccountId, buyerContactId])

    const handleAccountChange = useCallback(
        async (selectedAccount: DropdownObj | null) => {
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.ACCOUNT, selectedAccount)
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.BILLING_ADDRESS, null)

            if (!selectedAccount) {
                setBillingAddressOptions([])
                return
            }

            setIsFetchingBillingAddresses(true)

            try {
                const accountData = await getAccountDetails(selectedAccount.id).unwrap()

                if (accountData) {
                    const billingAddresses: DropdownObj[] =
                        accountData.billing_addresses?.map((addr: AccountBillingAddressDetails) => ({
                            id: addr.id,
                            title: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                        })) || []

                    setBillingAddressOptions(billingAddresses)
                }
            } catch (error) {
                console.error("Error fetching account details:", error)
                setBillingAddressOptions([])
            } finally {
                setIsFetchingBillingAddresses(false)
            }
        },
        [getAccountDetails, purchaseOrderFormik]
    )
    const addNewItem = () => {
        const currentItems = purchaseOrderFormik.values.items || []
        purchaseOrderFormik.setFieldValue("items", [...currentItems, createNewItem()])
    }

    const removeItem = (index: number) => {
        const currentItems = purchaseOrderFormik.values.items || []
        if (currentItems.length <= 1) {
            return // Prevent deletion if there's only one row
        }
        const newItems = currentItems.filter((_, i) => i !== index)
        purchaseOrderFormik.setFieldValue("items", newItems)
    }

    const updateItem = (index: number, field: EPurchaseOrderItemFields, value: any) => {
        const currentItems = purchaseOrderFormik.values.items || []
        const newItems = [...currentItems]
        newItems[index] = { ...newItems[index], [field]: value }

        // Calculate total amount when relevant fields change
        if (
            field === EPurchaseOrderItemFields.QUANTITY ||
            field === EPurchaseOrderItemFields.RATE ||
            field === EPurchaseOrderItemFields.DISCOUNT_VALUE ||
            field === EPurchaseOrderItemFields.DISCOUNT_TYPE ||
            field === EPurchaseOrderItemFields.TAX
        ) {
            const quantity = parseFloat(newItems[index][EPurchaseOrderItemFields.QUANTITY]) || 0
            const rate = parseFloat(newItems[index][EPurchaseOrderItemFields.RATE]) || 0
            const discountValue = parseFloat(newItems[index][EPurchaseOrderItemFields.DISCOUNT_VALUE]) || 0
            const discountType = newItems[index][EPurchaseOrderItemFields.DISCOUNT_TYPE]
            const tax = parseFloat(newItems[index][EPurchaseOrderItemFields.TAX]) || 0

            let subtotal = quantity * rate
            let discountAmount = 0

            if (discountType?.id === 1) {
                discountAmount = subtotal * (discountValue / 100)
            } else {
                discountAmount = discountValue
            }

            const afterDiscount = subtotal - discountAmount
            const taxAmount = afterDiscount * (tax / 100)
            const total = afterDiscount + taxAmount

            newItems[index][EPurchaseOrderItemFields.TOTAL_AMOUNT] = total.toFixed(2)
        }

        purchaseOrderFormik.setFieldValue("items", newItems)
    }

    const chargerColumns: MuiTableColumn<any>[] = [
        { id: "index", label: "S.No." },
        {
            id: "sku_code",
            label: "SKU Code",
            width: 150,
            render: (_, row, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        size="small"
                        containerHeight="100%"
                        options={chargerSkuOptions}
                        value={resolveSkuOption(chargerSkuOptions, row)}
                        onChange={(e: any) => updateChargerRow(rowIndex, "sku_code", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "display_name",
            label: "Display Name",
            width: 400,
            render: value => {
                return (
                    <Tooltip title={value}>
                        <Box
                            maxWidth={"300px"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                            whiteSpace={"nowrap"}
                            padding={"4px 8px"}>
                            {value || "-"}
                        </Box>
                    </Tooltip>
                )
            },
        },
        {
            id: "power_rating",
            label: "Ops Power Rating (KWh)*",
            width: 200,
            render: (value, _, rowIndex) => {
                const hasError = formik.submitCount > 0 && (!value || value === 0)
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateChargerRow(rowIndex, "power_rating", e.target.value)}
                        type="number"
                        size="small"
                        error={hasError}
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                            "& .MuiOutlinedInput-root": hasError
                                ? {
                                      "& fieldset": { borderColor: "#F94054" },
                                      "&:hover fieldset": { borderColor: "#F94054" },
                                  }
                                : {},
                        }}
                    />
                )
            },
        },
        {
            id: "branding",
            label: "Branding",
            width: 200,
            render: (value, _, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        disableClearable
                        size="small"
                        containerHeight="100%"
                        options={brandingOptions}
                        value={brandingOptions.find(brand => brand.id === value) || { id: "", title: "" }}
                        onChange={(e: any) => updateChargerRow(rowIndex, "branding", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "",
            width: 100,
            render: (_, row, rowIndex) => {
                return (
                    <Box display={"flex"} gap={1} alignItems={"center"} padding={"4px 8px"}>
                        <Box
                            width={"20px"}
                            height={"20px"}
                            display={"flex"}
                            justifyContent={"center"}
                            borderRadius={"50%"}
                            bgcolor={"#F940541A"}
                            alignItems={"center"}
                            sx={{
                                cursor: "pointer",
                            }}
                            onClick={() => handleDeleteChargerRow(rowIndex)}>
                            <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                        </Box>
                        {/* <CloneChargers onClone={count => handleCloneChargerRow(count, row)} /> */}
                    </Box>
                )
            },
        },
    ]

    const materialColumns: MuiTableColumn<any>[] = [
        { id: "index", label: "S.No." },
        {
            id: "sku_code",
            label: "SKU Code",
            width: 150,
            render: (_, row, rowIndex) => {
                return (
                    <MuiAutoComplete
                        disablePortal={false}
                        size="small"
                        containerHeight="100%"
                        options={materialSkuOptions}
                        value={resolveSkuOption(materialSkuOptions, row)}
                        onChange={(e: any) => updateMaterialRow(rowIndex, "sku_code", e?.id || "")}
                    />
                )
            },
        },
        {
            id: "description",
            label: "Description",
            width: 400,
            render: value => {
                return (
                    <Tooltip title={value}>
                        <Box
                            width={"150px"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                            whiteSpace={"nowrap"}
                            padding={"4px 8px"}>
                            {value || "-"}
                        </Box>
                    </Tooltip>
                )
            },
        },
        {
            id: "quantity",
            label: "Quantity*",
            render: (value, _, rowIndex) => {
                const hasError = formik.submitCount > 0 && (!value || value === 0)
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateMaterialRow(rowIndex, "quantity", e.target.value)}
                        type="number"
                        size="small"
                        error={hasError}
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                            "& .MuiOutlinedInput-root": hasError
                                ? {
                                      "& fieldset": { borderColor: "#F94054" },
                                      "&:hover fieldset": { borderColor: "#F94054" },
                                  }
                                : {},
                        }}
                    />
                )
            },
        },
        {
            id: "remarks",
            label: "Remarks",
            width: 150,
            render: (value, _, rowIndex) => {
                return (
                    <MuiTextField
                        value={value}
                        onChange={e => updateMaterialRow(rowIndex, "remarks", e.target.value)}
                        size="small"
                        sx={{
                            height: "100%",
                            "& .MuiInputBase-root": { borderRadius: 0 },
                            "& .MuiInputBase-input": { padding: "4px 8px" },
                        }}
                    />
                )
            },
        },
        {
            id: "custom",
            label: "",
            width: 100,
            render: (_, __, rowIndex) => {
                return (
                    <Box display={"flex"} gap={1} alignItems={"center"} padding={"4px 8px"}>
                        <Box
                            width={"20px"}
                            height={"20px"}
                            display={"flex"}
                            justifyContent={"center"}
                            borderRadius={"50%"}
                            bgcolor={"#F940541A"}
                            alignItems={"center"}
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleDeleteMaterialRow(rowIndex)}>
                            <FontAwesomeIcon icon={faTrashXmark} color="#F94054" size="sm" />
                        </Box>
                    </Box>
                )
            },
        },
    ]

    const handleParseMaterialsFile = async (files: FileList) => {
        if (files) {
            formik.setFieldValue(EChargersAndMaterialsFields.MATERIAL_FILE, files)
            const file = files[0]
            try {
                const formData = new FormData()
                formData.append("file", file)
                formData.append("project_id", project_id?.toString() || "117")
                const response = await parseMaterialsFile(formData).unwrap()
                const materialRows: MaterialRow[] = (response?.rows ?? []).map(row => ({
                    sku_code: row.sku_code || "",
                    description: row.description || "",
                    quantity: row.quantity || 0,
                    remarks: row.remarks || "",
                }))
                if (materialRows.length > 0) {
                    chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.MATERIALS, materialRows)
                    setShowMoreMaterials(true)
                }
                announceParseResult("material", response, materialRows.length)
            } catch (error) {
                console.error("Error parsing materials file:", error)
                enqueueSnackbar("Error parsing materials file", {
                    variant: "error",
                })
            }
        }
    }

    const handleParseChargersFile = async (files: FileList) => {
        if (files) {
            formik.setFieldValue(EChargersAndMaterialsFields.CHARGER_FILE, files)
            const file = files[0]
            try {
                const formData = new FormData()
                formData.append("file", file)
                formData.append("project_id", project_id?.toString() || "117")
                const response = await parseChargersFile(formData).unwrap()
                const chargerRows = expandParsedChargerRows(response?.rows ?? [])
                if (chargerRows.length > 0) {
                    chargerAndMaterialsFormik.setFieldValue(EChargersAndMaterialsFields.CHARGERS, chargerRows)
                    setShowMoreChargers(true)
                }
                announceParseResult("charger", response, chargerRows.length)
            } catch (error) {
                console.error("Error parsing chargers file:", error)
                enqueueSnackbar("Error parsing chargers file", {
                    variant: "error",
                })
            }
        }
    }

    const openPurchaseOrderForm = async () => {
        console.log("full state", account_n_sites_form_values)
        if (formik.values[ELogisticBasicDetailFields.PURCHASE_ORDER]) {
            formik.setFieldValue(ELogisticBasicDetailFields.PURCHASE_ORDER, null)
        }

        // Prefill Buyer account and billing address from Account & Location page
        const buyerData = account_n_sites_form_values?.accountRoles?.Buyer
        if (buyerData?.account) {
            const buyerAccount = {
                id: buyerData.account.id as number,
                title: buyerData.account.title,
            }
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.ACCOUNT, buyerAccount)

            // Fetch billing addresses for the buyer account
            setIsFetchingBillingAddresses(true)
            try {
                const accountData = await getAccountDetails(buyerAccount.id).unwrap()

                if (accountData) {
                    const billingAddresses: DropdownObj[] =
                        accountData.billing_addresses?.map((addr: AccountBillingAddressDetails) => ({
                            id: addr.id,
                            title: `${addr.address}, ${addr.city}, ${addr.state} - ${addr.pincode}`,
                        })) || []

                    setBillingAddressOptions(billingAddresses)

                    // Prefill billing address if it exists in buyer data
                    if (buyerData.billing_address) {
                        const matchingAddress = billingAddresses.find(addr => addr.id === buyerData.billing_address?.id)
                        if (matchingAddress) {
                            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.BILLING_ADDRESS, matchingAddress)
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching account details:", error)
                setBillingAddressOptions([])
            } finally {
                setIsFetchingBillingAddresses(false)
            }
        }

        setShowPurchaseOrderForm(true)
    }

    const closePurchaseOrderForm = () => {
        setShowPurchaseOrderForm(false)
    }

    async function handleSubmit() {
        const basicFormikValues = formik.values

        if (showPurchaseOrderForm) {
            const poErrors = await purchaseOrderFormik.validateForm()
            if (Object.keys(poErrors).length > 0) {
                return
            }
        }

        let finalFormValues = { ...basicFormikValues }

        if (showPurchaseOrderForm && project_id) {
            const poValues = purchaseOrderFormik.values
            const products = poValues.items.map(item => ({
                product_id: item[EPurchaseOrderItemFields.SKU_CODE]?.id,
                quantity: parseFloat(item[EPurchaseOrderItemFields.QUANTITY]) || 0,
                rate: parseFloat(item[EPurchaseOrderItemFields.RATE]) || 0,
                discount: parseFloat(item[EPurchaseOrderItemFields.DISCOUNT_VALUE]) || 0,
                discount_unit: item[EPurchaseOrderItemFields.DISCOUNT_TYPE]?.id === 1 ? "percent" : "amount",
                tax_percent: parseFloat(item[EPurchaseOrderItemFields.TAX]) || 0,
                total_amount: parseFloat(item[EPurchaseOrderItemFields.TOTAL_AMOUNT]) || 0,
            }))

            const formData = new FormData()
            formData.append("po_name", poValues[EPurchaseOrderFields.PO_NAME])
            formData.append("po_number", poValues[EPurchaseOrderFields.PO_NUMBER])
            formData.append("po_amount", poValues[EPurchaseOrderFields.TOTAL_PO_VALUE] || "0")
            formData.append("shipping_charges", poValues[EPurchaseOrderFields.SHIPPING_CHARGES] || "0")
            formData.append("account_id", String(poValues[EPurchaseOrderFields.ACCOUNT]?.id || ""))
            formData.append("uploaded_by", String(poValues[EPurchaseOrderFields.PO_DEAL_DONE_BY]?.id || ""))
            formData.append("po_created_at", poValues[EPurchaseOrderFields.PO_DATE])
            formData.append("remarks", poValues[EPurchaseOrderFields.REMARKS] || "")
            formData.append("project_id", String(project_id))
            formData.append("module_id", String(EModuleIds.LOGISTICS))
            formData.append("products", JSON.stringify(products))

            if (poValues[EPurchaseOrderFields.PO_FILE] !== null) {
                formData.append("file", poValues[EPurchaseOrderFields.PO_FILE] as File)
            }

            const poResult = await addPurchaseOrder(formData).unwrap()

            if (poResult?.po_id) {
                finalFormValues = {
                    ...basicFormikValues,
                    [ELogisticBasicDetailFields.PURCHASE_ORDER]: {
                        id: poResult.po_id,
                        title: `${poValues[EPurchaseOrderFields.PO_NAME]}${poValues[EPurchaseOrderFields.PO_NUMBER] ? ` (${poValues[EPurchaseOrderFields.PO_NUMBER]})` : ""}`,
                    },
                }
            }
        }

        // Which surface produced the link. Reporting only — the backend re-derives the category
        // and complaint from the drawer context when the source is COMPLAINT_DRAWER rather than
        // trusting these values.
        const complaintLinkSource = finalFormValues[ELogisticBasicDetailFields.COMPLAINT]
            ? complaint_link_context
                ? EComplaintLinkSource.COMPLAINT_DRAWER
                : EComplaintLinkSource.LOGISTICS_FORM
            : null

        dispatch(setLogisticsBasicDetailsForm({ ...finalFormValues, complaint_link_source: complaintLinkSource }))

        const hasPurchaseOrder =
            basicFormikValues[ELogisticBasicDetailFields.PURCHASE_ORDER]?.id || showPurchaseOrderForm
        if (props.isCPODeal) {
            if (hasPurchaseOrder && props.addPaymentPage) {
                props.addPaymentPage()
            } else if (!hasPurchaseOrder && props.removePaymentPage) {
                props.removePaymentPage()
            }
        }

        props.handleNextStep()
    }

    const hasError = (field: ELogisticBasicDetailFields) => Boolean(formik.submitCount > 0 && formik.errors[field])
    const hasPocError = (
        pocField: ELogisticBasicDetailFields.SOURCE_POC | ELogisticBasicDetailFields.DESTINATION_POC,
        key: keyof IPocDetailsForm
    ) =>
        Boolean(
            formik.submitCount > 0 && (formik.errors[pocField] as FormikErrors<IPocDetailsForm> | undefined)?.[key]
        )
    const hasPurchaseOrderError = (field: EPurchaseOrderFields) =>
        Boolean((formik.submitCount > 0 || purchaseOrderFormik.touched[field]) && purchaseOrderFormik.errors[field])

    const hasItemsTableError = useMemo(() => {
        if (formik.submitCount === 0) return false
        const items = purchaseOrderFormik.values.items
        // Check if all items have empty required fields (no valid item)
        return items.every(
            item =>
                !item[EPurchaseOrderItemFields.SKU_CODE] &&
                !item[EPurchaseOrderItemFields.QUANTITY] &&
                !item[EPurchaseOrderItemFields.RATE]
        )
    }, [formik.submitCount, purchaseOrderFormik.values.items])

    const getItemFieldError = useCallback(
        (index: number, field: EPurchaseOrderItemFields): boolean => {
            if (formik.submitCount === 0) return false
            const item = purchaseOrderFormik.values.items[index]
            if (!item) return false

            // Check if the field is empty when it should have a value
            switch (field) {
                case EPurchaseOrderItemFields.SKU_CODE:
                    return !item[EPurchaseOrderItemFields.SKU_CODE]
                case EPurchaseOrderItemFields.QUANTITY:
                    return !item[EPurchaseOrderItemFields.QUANTITY] || item[EPurchaseOrderItemFields.QUANTITY] === ""
                case EPurchaseOrderItemFields.RATE:
                    return !item[EPurchaseOrderItemFields.RATE] || item[EPurchaseOrderItemFields.RATE] === ""
                default:
                    return false
            }
        },
        [formik.submitCount, purchaseOrderFormik.values.items]
    )

    const handlePOFileChange = (files: FileList) => {
        if (files && files.length > 0) {
            purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.PO_FILE, files[0])
        }
    }

    useEffect(() => {
        purchaseOrderFormik.setFieldValue(EPurchaseOrderFields.TOTAL_PO_VALUE, calculatedTotalPOValue)
    }, [calculatedTotalPOValue])

    return {
        states: {
            formik,
            purchaseOrderOptions,
            categoryOptions,
            isCategoriesFetching,
            timeOfDayOptions,
            showPurchaseOrderForm,
            purchaseOrderFormik,
            accountsTrigger: getAccountsCompacted,
            billingAddressOptions,
            isFetchingBillingAddresses,
            dealDoneByUsersOptions,
            showMoreChargers,
            chargerColumns,
            chargerAndMaterialsFormik,
            materialColumns,
            showMoreMaterials,
            isFetchingChargerTemplate,
            isFetchingMaterialTemplate,
            skuOptions,
            // Complaint linkage
            isComplaintLocked,
            isComplaintLinkedCategorySelected,
            selectedComplaint,
            selectedComplaintSummary,
            complaintsTrigger: getComplaintsForPicker,
            complaintPickerPageSize: COMPLAINT_PICKER_PAGE_SIZE,
            existingLinkedCount,
            isCategoryChangeConfirmOpen,
            pendingCategory,
        },
        functions: {
            openPurchaseOrderForm,
            handleCategoryChange,
            handleComplaintChange,
            complaintOptionsSelector,
            confirmCategoryChange,
            cancelCategoryChange,
            closePurchaseOrderForm,
            hasPurchaseOrderError,
            handleAddChargerRow,
            handleShowMoreChargers,
            handleShowMoreMaterials,
            handleAddMaterialRow,
            handleDownloadTemplate,
            handleParseChargersFile,
            handleParseMaterialsFile,
            handleSubmit,
            hasError,
            hasPocError,
            addNewItem,
            removeItem,
            updateItem,
            hasItemsTableError,
            getItemFieldError,
            handleAccountChange,
        },
    }
}

export default useLogisticBasicDetails
